import React, { useState } from 'react';
import { 
  X, Printer, Check, QrCode, Eye 
} from 'lucide-react';
import { generateCode128Svg } from '../utils/barcode128';
import { LABEL_SIZES, printThermalLabels } from '../utils/thermalLabelHelper';
import toast from 'react-hot-toast';

const UnifiedLabelPrintModal = ({
  isOpen,
  onClose,
  labels = [],
  title = 'Impressão de Etiquetas de Peças',
  sourceType = 'CUTTING_PLAN' // 'CUTTING_PLAN', 'STOCK', 'RETALHO'
}) => {
  // Configurações de Formato de Etiqueta
  const [selectedSize, setSelectedSize] = useState('60x40');

  // Opções Opcionais com Chaves (Toggles)
  const [showEdgeBanding, setShowEdgeBanding] = useState(true);
  const [showGrain, setShowGrain] = useState(true);
  const [showClientProject, setShowClientProject] = useState(true);
  const [showBarcode128, setShowBarcode128] = useState(true);
  const [showQrCode, setShowQrCode] = useState(true);

  if (!isOpen || !labels || labels.length === 0) return null;

  const currentSizeConfig = LABEL_SIZES[selectedSize] || LABEL_SIZES['60x40'];

  /**
   * Gera o SVG ou diagrama compacto de fita de borda (E1, E2, L1, L2)
   */
  const renderEdgeBandingSchema = (label) => {
    // Determinar quais bordas recebem fita
    const edges = {
      top: Boolean(label.edge_top || label.fitas?.topo || label.fita_superior),
      bottom: Boolean(label.edge_bottom || label.fitas?.base || label.fita_inferior),
      left: Boolean(label.edge_left || label.fitas?.esquerda || label.fita_esquerda),
      right: Boolean(label.edge_right || label.fitas?.direita || label.fita_direita)
    };

    const hasAnyEdge = edges.top || edges.bottom || edges.left || edges.right;

    return (
      <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded p-1">
        {/* Retângulo esquemático da peça */}
        <div className="relative w-8 h-6 border border-gray-400 bg-white rounded-[2px] flex items-center justify-center">
          {/* Top Edge */}
          <div className={`absolute top-0 left-0 right-0 h-[2px] ${edges.top ? 'bg-black' : 'bg-transparent'}`} />
          {/* Bottom Edge */}
          <div className={`absolute bottom-0 left-0 right-0 h-[2px] ${edges.bottom ? 'bg-black' : 'bg-transparent'}`} />
          {/* Left Edge */}
          <div className={`absolute top-0 bottom-0 left-0 w-[2px] ${edges.left ? 'bg-black' : 'bg-transparent'}`} />
          {/* Right Edge */}
          <div className={`absolute top-0 bottom-0 right-0 w-[2px] ${edges.right ? 'bg-black' : 'bg-transparent'}`} />
          <span className="text-[7px] font-mono font-bold text-gray-500">FB</span>
        </div>
        <div className="text-[8px] leading-tight text-gray-700">
          <p className="font-bold">{hasAnyEdge ? 'Fitas de Borda:' : 'Sem fita'}</p>
          <p className="text-[7px] text-gray-500">
            {[
              edges.top && 'T',
              edges.bottom && 'B',
              edges.left && 'E',
              edges.right && 'D'
            ].filter(Boolean).join(' • ') || 'Nenhuma borda'}
          </p>
        </div>
      </div>
    );
  };

  /**
   * Constrói o HTML puro de cada etiqueta individual para a impressora térmica
   */
  const buildLabelHtml = (label) => {
    const code = label.label_code || label.qr_code || label.codigo_etiqueta || `LBL-${label.id}`;
    const name = label.part_name || label.produto_nome || label.nome || 'Peça Cortada';
    const dims = (label.width && label.height) 
      ? `${label.width} × ${label.height} mm`
      : (label.comprimento_cm && label.largura_cm)
      ? `${label.comprimento_cm} × ${label.largura_cm} cm`
      : (label.tamanho_label || 'Padrão');

    const board = label.board_number ? `Chapa #${label.board_number}` : '';
    const thickness = label.thickness || label.espessura_mm ? `${label.thickness || label.espessura_mm}mm` : '';
    const material = label.material_name || label.cor || '';

    // Sentido do veio
    const grainInfo = showGrain && (label.grain_direction || label.sentido_veio) ? (
      `<div style="font-size: 7px; color: #333; margin-top: 1px;"><strong>Veio:</strong> ${label.grain_direction || label.sentido_veio}</div>`
    ) : '';

    // Cliente e Projeto
    const clientProjectHtml = showClientProject && (label.client_name || label.project_name) ? (
      `<div style="font-size: 7.5px; font-weight: bold; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; border-bottom: 1px dashed #666; padding-bottom: 1px; margin-bottom: 2px;">
        ${[label.project_name, label.client_name].filter(Boolean).join(' - ')}
      </div>`
    ) : '';

    // Código de Barras Linear Code 128
    const barcodeHtml = showBarcode128 ? (
      `<div style="text-align: center; margin: 1.5mm 0 0.5mm 0; max-height: 22px; overflow: hidden;">
        ${generateCode128Svg(code, { height: 18, barWidth: selectedSize === '50x50' ? 0.9 : 1.1, showText: false })}
      </div>`
    ) : '';

    // URL para QR Code (link para leitura com câmera do celular)
    const baseUrl = window.location.origin;
    const qrUrl = `${baseUrl}/etiqueta/${encodeURIComponent(code)}`;
    const qrCodeApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&margin=0&data=${encodeURIComponent(qrUrl)}`;

    return `
      <div class="thermal-label-page">
        <!-- Topo: Projeto / Cliente -->
        ${clientProjectHtml}

        <!-- Informações Principais da Peça -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 4px;">
          <div style="flex: 1; overflow: hidden;">
            <div style="font-size: 10px; font-weight: 800; line-height: 1.1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
              ${name}
            </div>
            <div style="font-size: 9px; font-weight: bold; color: #111; margin-top: 1px;">
              ${dims} ${thickness ? `<span style="font-weight: normal; font-size: 8px;">(${thickness})</span>` : ''}
            </div>
            ${material ? `<div style="font-size: 7.5px; color: #444; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${material} ${board ? `• ${board}` : ''}</div>` : ''}
            ${grainInfo}
          </div>

          <!-- QR Code Lateral -->
          ${showQrCode ? `
            <div style="width: 14mm; height: 14mm; flex-shrink: 0; text-align: center;">
              <img src="${qrCodeApiUrl}" style="width: 100%; height: 100%; display: block;" alt="QR" />
            </div>
          ` : ''}
        </div>

        <!-- Indicador de Fitas de Borda Opcional -->
        ${showEdgeBanding ? `
          <div style="display: flex; align-items: center; justify-content: space-between; font-size: 7px; background: #eee; padding: 1px 3px; border-radius: 2px; margin: 1mm 0 0 0;">
            <span><strong>Fitas Borda:</strong> ${[
              (label.edge_top || label.fitas?.topo) && 'Topo (T)',
              (label.edge_bottom || label.fitas?.base) && 'Base (B)',
              (label.edge_left || label.fitas?.esquerda) && 'Esq (E)',
              (label.edge_right || label.fitas?.direita) && 'Dir (D)'
            ].filter(Boolean).join(' • ') || 'Sem fita de borda'}</span>
          </div>
        ` : ''}

        <!-- Código de Barras Linear Code 128 -->
        ${barcodeHtml}

        <!-- Rodapé da Etiqueta -->
        <div style="display: flex; justify-content: space-between; align-items: center; font-size: 7px; font-family: monospace; font-weight: bold; border-top: 1px solid #000; padding-top: 1px; margin-top: auto;">
          <span>${code}</span>
          <span style="font-size: 6px; font-weight: normal; font-family: sans-serif;">MARCENARIA SANTIAGO</span>
        </div>
      </div>
    `;
  };

  /**
   * Dispara a impressão térmica formatada
   */
  const handlePrintAll = () => {
    try {
      const allLabelsHtml = labels.map(label => buildLabelHtml(label)).join('');
      printThermalLabels({
        htmlContent: allLabelsHtml,
        sizeId: selectedSize,
        title: `${title} (${labels.length} etiquetas)`
      });
      toast.success(`${labels.length} etiqueta(s) enviada(s) para impressão!`);
      onClose();
    } catch (err) {
      console.error('Erro na impressão térmica:', err);
      toast.error('Erro ao gerar impressão térmica.');
    }
  };

  const sampleLabel = labels[0] || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-100">
        
        {/* Cabeçalho do Modal */}
        <div className="px-6 py-4 border-b border-gray-200 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-600 text-white flex items-center justify-center shadow-sm">
              <Printer className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-500">
                {labels.length} etiqueta(s) pronta(s) para impressão na oficina
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200/60 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corpo com Configurações e Pré-visualização */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Seletor de Tamanhos de Etiqueta */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-2">
              1. Selecione o Padrão da Etiqueta Térmica:
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {Object.values(LABEL_SIZES).map((size) => {
                const isSelected = selectedSize === size.id;
                return (
                  <button
                    key={size.id}
                    type="button"
                    onClick={() => setSelectedSize(size.id)}
                    className={`text-left p-3 rounded-xl border transition-all ${
                      isSelected
                        ? 'border-amber-600 bg-amber-50/70 ring-2 ring-amber-600/20'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm text-gray-900">{size.name}</span>
                      {isSelected && <Check className="w-4 h-4 text-amber-600" />}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">{size.recommendedFor}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Chaves de Ativação / Toggles Opcionais */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-2">
              2. Informações Opcionais na Etiqueta (Ligar / Desligar):
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
              
              {/* Toggle Fitas de Borda */}
              <label className="flex items-center justify-between cursor-pointer p-1">
                <div>
                  <span className="text-xs font-semibold text-gray-800 block">Esquema de Fitas de Borda</span>
                  <span className="text-[11px] text-gray-500">Mostra lados para coladeira (E1, E2, L1, L2)</span>
                </div>
                <input
                  type="checkbox"
                  checked={showEdgeBanding}
                  onChange={(e) => setShowEdgeBanding(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                />
              </label>

              {/* Toggle Sentido do Veio */}
              <label className="flex items-center justify-between cursor-pointer p-1">
                <div>
                  <span className="text-xs font-semibold text-gray-800 block">Sentido do Veio da Madeira</span>
                  <span className="text-[11px] text-gray-500">Indica a direção correta do veio no corte</span>
                </div>
                <input
                  type="checkbox"
                  checked={showGrain}
                  onChange={(e) => setShowGrain(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                />
              </label>

              {/* Toggle Cliente / Projeto */}
              <label className="flex items-center justify-between cursor-pointer p-1">
                <div>
                  <span className="text-xs font-semibold text-gray-800 block">Nome do Cliente e Projeto</span>
                  <span className="text-[11px] text-gray-500">Evita misturar peças de pedidos diferentes</span>
                </div>
                <input
                  type="checkbox"
                  checked={showClientProject}
                  onChange={(e) => setShowClientProject(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                />
              </label>

              {/* Toggle Código de Barras Linear Code 128 */}
              <label className="flex items-center justify-between cursor-pointer p-1">
                <div>
                  <span className="text-xs font-semibold text-gray-800 block">Código de Barras (Code 128)</span>
                  <span className="text-[11px] text-gray-500">Para bipar com pistola laser USB</span>
                </div>
                <input
                  type="checkbox"
                  checked={showBarcode128}
                  onChange={(e) => setShowBarcode128(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                />
              </label>

              {/* Toggle QR Code */}
              <label className="flex items-center justify-between cursor-pointer p-1 sm:col-span-2 border-t border-gray-200/80 pt-2">
                <div>
                  <span className="text-xs font-semibold text-gray-800 block">QR Code para Leitura no Celular</span>
                  <span className="text-[11px] text-gray-500">Ao bipar com o celular abre a ficha técnica da peça</span>
                </div>
                <input
                  type="checkbox"
                  checked={showQrCode}
                  onChange={(e) => setShowQrCode(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                />
              </label>

            </div>
          </div>

          {/* Pré-visualização da Etiqueta Modelo */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-amber-600" />
                Prévia da 1ª Etiqueta ({currentSizeConfig.name}):
              </label>
              <span className="text-[11px] text-gray-500 font-mono">
                {sampleLabel.label_code || sampleLabel.qr_code || sampleLabel.codigo_etiqueta || 'EXEMPLO-001'}
              </span>
            </div>

            <div className="bg-slate-100 p-4 rounded-xl flex items-center justify-center border border-gray-200">
              <div 
                className="bg-white text-black p-2.5 rounded shadow-sm border border-gray-400 flex flex-col justify-between"
                style={{
                  width: `${Math.min(currentSizeConfig.widthMm * 5.2, 360)}px`,
                  minHeight: `${currentSizeConfig.heightMm * 4.2}px`
                }}
              >
                {/* Cabeçalho Prévia */}
                {showClientProject && (
                  <div className="text-[10px] font-bold text-gray-800 border-b border-dashed border-gray-400 pb-1 mb-1 truncate">
                    {sampleLabel.project_name || 'Projeto Móveis Sala'} - {sampleLabel.client_name || 'Cliente Exemplo'}
                  </div>
                )}

                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-xs font-extrabold text-gray-900 truncate">
                      {sampleLabel.part_name || sampleLabel.produto_nome || 'Lateral Esquerda Balcão'}
                    </p>
                    <p className="text-[11px] font-bold text-gray-900">
                      {sampleLabel.width && sampleLabel.height 
                        ? `${sampleLabel.width} × ${sampleLabel.height} mm`
                        : `${sampleLabel.comprimento_cm || 70} × ${sampleLabel.largura_cm || 45} cm`}
                    </p>
                    <p className="text-[9px] text-gray-600">
                      {sampleLabel.material_name || sampleLabel.cor || 'MDF Branco 15mm'}
                    </p>
                    {showGrain && (
                      <p className="text-[8px] text-gray-500 mt-0.5">Veio: No sentido do comprimento</p>
                    )}
                  </div>

                  {showQrCode && (
                    <div className="w-10 h-10 border border-gray-300 rounded flex items-center justify-center p-0.5 shrink-0 bg-gray-50">
                      <QrCode className="w-8 h-8 text-gray-800" />
                    </div>
                  )}
                </div>

                {/* Fitas de borda na prévia */}
                {showEdgeBanding && (
                  <div className="mt-1">
                    {renderEdgeBandingSchema(sampleLabel)}
                  </div>
                )}

                {/* Código de barras na prévia */}
                {showBarcode128 && (
                  <div className="my-1.5 flex flex-col items-center">
                    <div 
                      dangerouslySetInnerHTML={{ 
                        __html: generateCode128Svg(
                          sampleLabel.label_code || sampleLabel.qr_code || sampleLabel.codigo_etiqueta || 'EXEMPLO-001', 
                          { height: 20, barWidth: 1.1, showText: false }
                        ) 
                      }} 
                    />
                  </div>
                )}

                {/* Rodapé prévia */}
                <div className="flex items-center justify-between text-[8px] font-mono border-t border-black pt-0.5 mt-auto">
                  <span className="font-bold">{sampleLabel.label_code || sampleLabel.qr_code || 'EXEMPLO-001'}</span>
                  <span className="text-[7px] text-gray-600">SANTIAGO</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Rodapé do Modal com Botões */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors"
          >
            Cancelar
          </button>
          
          <button
            type="button"
            onClick={handlePrintAll}
            className="px-6 py-2.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-amber-600/30 flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Imprimir {labels.length} Etiqueta(s) Térmica(s)
          </button>
        </div>

      </div>
    </div>
  );
};

export default UnifiedLabelPrintModal;
